import { ComponentFactoryResolver, Injectable, Injector, ViewContainerRef, ComponentRef } from '@angular/core';
import { SprintcardComponent } from './sprintcard.component';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { InteractionService } from '../../services/interaction.service';
import { SynchronizationService } from '../../services/synchronization.service';
import { HTMLElementPositioningHelper } from '../../util/htmlelement-positioning-helper';
import interact from 'interactjs';
import { EventSprintcardOpened } from '../../models/syncEvents/realtimeEvents/eventSprintcardOpened';
import { SprintService } from '../../services/sprint.service';
import { Sprint } from '../../models/sprint';
import { EventSprintcardMoved } from '../../models/syncEvents/realtimeEvents/eventSprintcardMoved';
import { EventSprintcardToForeground } from '../../models/syncEvents/realtimeEvents/eventSprintcardToForeground';
import { ReleaseplanningSprintColumn } from '../releaseplanning/releaseplanning-sprint-column/releaseplanning-sprint-column.component';

/**
 * Helper class encapsulating functions to create Sprintcards to edit or create new sprints
 */
@Injectable()
export class SprintcardHelper {

  public constructor(
    private sprintService: SprintService,
    private interactionService: InteractionService,
    private syncService: SynchronizationService,
    private resolver: ComponentFactoryResolver,
    private positionHelper: HTMLElementPositioningHelper,
    private dynamicElementService: DynamicElementService
  ) { }

  /**
  * Open a sprintCard for the creation of a new sprint
  *
  * @param mouseEvent use to set the position of the new card to the mouseevents coordinates
  * @param position use to set the position of the new card to this relative position (from sync event)
  */
  openNewSprintDialog(
    projectKey: string,
    onNewSprintCreatedCallback?,
    mouseEvent?: MouseEvent,
    syncEvent?: EventSprintcardOpened): void {

    // If we react to a sync event we use the new-sprintId of the event, else we create one
    const newSprintId: number = (syncEvent) ? syncEvent.sprintId : this.sprintService.getTemporaryIdForNewSprint();
    const newSprintName: string = this.getDefaultNewSprintName(projectKey);
    const newSprint: Sprint = new Sprint(newSprintId, newSprintName, null, null, 'future');
    const card: SprintcardComponent = this.createCard(newSprint, projectKey, null, onNewSprintCreatedCallback, mouseEvent, syncEvent);

    // Only send sync-Event if we are not reacting to a sync event ourselves (else we create a loop)
    if (!syncEvent) {
      const position = this.positionHelper.getRelativePositionOfElement(card.getElementRef().nativeElement);
      const event = new EventSprintcardOpened(card.sprint.id, position);
      this.syncService.sendRealTimeEventNew(event);
    }
  }

  private getDefaultNewSprintName(projectKey: string): string {
    return projectKey + ' Sprint ';
  }

  /**
   * Create a draggable sprintcard for an existing sprint if such a card doesnt already exist for this sprint
   *
   * @param sprint the sprint to be displayed in the sprintcard
   * @param projectKey
   * @param onCloseCallback callback that should be called when the card is closed
   */
  createCard(
    sprint: Sprint,
    projectKey: string,
    sprintColumnComponent?: ReleaseplanningSprintColumn,
    onNewSprintCreatedCallback?,
    mouseEvent?: MouseEvent,
    syncEvent?: EventSprintcardOpened): SprintcardComponent {

    // Check if a card for this sprint already exists ...
    const existingCard = this.dynamicElementService.getSprintcard(sprint.id);
    if (existingCard) {
      return existingCard;
    }
    // ... if not create a new card
    // If parameter 'sprintColumnComponent' is set use it for callback/Event when a sprint is removed
    const onCreatedRemoved = (sprintColumnComponent) ?
      (sprintId) => { sprintColumnComponent.sprintRemoved.emit(sprintId); } : () => { };

    const injector = Injector.create({
      providers: [
        { provide: 'sprint', useValue: sprint },
        { provide: 'projectKey', useValue: projectKey },
        { provide: 'onCloseCallback', useValue: (sprintcard) => this.removeCardFromGlobalReferences(sprintcard) },
        { provide: 'onNewSprintCreatedCallback', useValue: (createdSprint) => onNewSprintCreatedCallback(createdSprint) },
        { provide: 'onSprintRemovedCallback', useValue: onCreatedRemoved }
      ]
    });
    const insertionPoint: ViewContainerRef = this.dynamicElementService.getInsertionPoint();
    const componentRef: ComponentRef<SprintcardComponent> = insertionPoint.createComponent(SprintcardComponent, { injector: injector });
    const card: SprintcardComponent = componentRef.instance;
    card.componentRef = componentRef;

    this.setupSprintcardAsDraggable(card);
    this.setupSprintcardAsClickable(card);
    this.positionAndFocusNewCard(card, mouseEvent, syncEvent);
    this.dynamicElementService.insert(componentRef);
    return card;
  }

  setupSprintcardAsDraggable(sprintcard: SprintcardComponent): void {
    const cardElement: HTMLElement = sprintcard.getElementRef().nativeElement;
    const cardHeaderElement: HTMLElement = sprintcard.getElementRef().nativeElement.querySelector('div.dialog__header');
    this.interactionService.setUpDraggable(cardHeaderElement, cardElement, this.getOnSprintcardMovedSyncFunction(sprintcard));
  }

  setupSprintcardAsClickable(sprintcard: SprintcardComponent): void {
    this.interactionService.addClickInteractionElement(sprintcard.getElementRef(), this.getOnSprintcardClickedSyncFunction(sprintcard));
  }

  /**
   * @param mouseEvent use to set the position of the new card according to mouseevent coordinates
   * @param position use to set the position of the new card to this relative position (from sync event)
   */
  private positionAndFocusNewCard(card: SprintcardComponent, mouseEvent?: MouseEvent, syncEvent?: EventSprintcardOpened): void {
    const cardElement = card.getElementRef().nativeElement;

    if (mouseEvent) {
      const pos = this.positionHelper.getPositionForNewCard(mouseEvent, cardElement);
      this.positionHelper.setAbsolutePosition(cardElement, pos.x, pos.y);
    }
    if (syncEvent && !mouseEvent) {
      this.positionHelper.setRelativePosition(cardElement, syncEvent.position.left, syncEvent.position.top);
    }

    // Set focus on the title-input-field of the card
    cardElement.querySelector('input#dialog-title').focus();
  }


  // TODO duplicate code to issuecard.helper. move to dynamic element service?
  /**
   * Removes the card from dynamicElementService and unregisters global interactjs-listeners on the card.
   * Note that you might still have to do some removal-actions on a local (board-view) level.
   */
  removeCardFromGlobalReferences(sprintcard: SprintcardComponent): void {
    // Remove interact listeners from card (draggable)
    // Since update to interactjs 1.4.0 this can throw an error, see:
    // https://www.bountysource.com/issues/73500059-cannot-set-property-activedrops-of-undefined
    // Waiting for new interactjs version to fix this
    try {
      interact(sprintcard.componentRef.instance.getElementRef().nativeElement).unset();
    } catch {
      // Nothing to do
    }
    this.dynamicElementService.remove(sprintcard.componentRef);
  }

  private getOnSprintcardMovedSyncFunction(sprintcard: SprintcardComponent): any {
    const syncMoveFunction = (event: any): void => {
      this.dynamicElementService.select(sprintcard.componentRef);
      const sprintId = event.target.getAttribute('data-sprintid');
      const position = this.positionHelper.getRelativePositionOfElement(sprintcard.getElementRef().nativeElement);
      this.syncService.sendRealTimeEventNew(new EventSprintcardMoved(sprintId, position));
    };
    return syncMoveFunction;
  }

  private getOnSprintcardClickedSyncFunction(sprintcard: SprintcardComponent): any {
    const syncClickFunction = (): void => {
      this.dynamicElementService.select(sprintcard.componentRef);
      this.syncService.sendRealTimeEventNew(new EventSprintcardToForeground(sprintcard.sprint.id));
    };
    return syncClickFunction;
  }

}
